import { config } from '@/lib/config';

let apiLocale: 'ru' | 'kz' | 'en' = 'ru';
export const setApiLocale = (locale: 'ru' | 'kz' | 'en'): void => { apiLocale = locale; };
const unavailableMessage = (): string => ({
  ru: 'Сервис временно недоступен', kz: 'Қызмет уақытша қолжетімсіз', en: 'The service is temporarily unavailable',
})[apiLocale];
const responseErrorMessage = (status: number): string => status === 413 ? ({
  ru: 'Размер файла превышает допустимый лимит загрузки',
  kz: 'Файл өлшемі жүктеу шегінен асады',
  en: 'The file exceeds the upload size limit',
})[apiLocale] : unavailableMessage();

const parsePayload = (raw: string): unknown => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return undefined; }
};

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type TokenProvider = () => string | null;

let platformTokenProvider: TokenProvider = () => null;
let boardTokenProvider: TokenProvider = () => null;
let unauthorizedHandler: (() => void) | null = null;

export const setPlatformTokenProvider = (provider: TokenProvider): void => {
  platformTokenProvider = provider;
};

export const setBoardTokenProvider = (provider: TokenProvider): void => {
  boardTokenProvider = provider;
};

export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  unauthorizedHandler = handler;
};

export type AuthChannel = 'none' | 'platform' | 'board';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  channel?: AuthChannel;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}




const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const url = `${config.apiBaseUrl}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  const serialized = params.toString();
  return serialized ? `${url}?${serialized}` : url;
};

const extractDetail = (payload: unknown): string | null => {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0];
      if (first && typeof first === 'object' && 'msg' in first) {
        return String((first as { msg: unknown }).msg);
      }
    }
  }
  return null;
};

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', body, channel = 'none', query, signal, headers: extraHeaders } = options;
  const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': apiLocale, ...extraHeaders };

  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (channel === 'platform') {
    const token = platformTokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  if (channel === 'board') {
    const token = boardTokenProvider();
    if (token) headers['X-Board-Token'] = token;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch {
    throw new ApiError(0, unavailableMessage());
  }

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  const payload = parsePayload(raw);

  if (!response.ok) {
    if (response.status === 401 && channel === 'platform') unauthorizedHandler?.();
    throw new ApiError(response.status, extractDetail(payload) ?? responseErrorMessage(response.status));
  }

  if (raw && payload === undefined) throw new ApiError(response.status, unavailableMessage());
  return payload as T;
};

export const apiUpload = async <T>(path: string, file: File, field = 'file', fields: Record<string, string> = {}): Promise<T> => {
  const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': apiLocale };
  const token = platformTokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;
  const body = new FormData();
  body.append(field, file);
  Object.entries(fields).forEach(([key, value]) => body.append(key, value));
  let response: Response;
  try {
    response = await fetch(buildUrl(path), { method: 'POST', headers, body });
  } catch {
    throw new ApiError(0, unavailableMessage());
  }
  const raw = await response.text();
  const payload = parsePayload(raw);
  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.();
    throw new ApiError(response.status, extractDetail(payload) ?? responseErrorMessage(response.status));
  }
  if (raw && payload === undefined) throw new ApiError(response.status, unavailableMessage());
  return payload as T;
};

export type BlobDownload = {
  blob: Blob;
  filename: string | null;
};

export const apiRequestBlob = async (
  path: string,
  options: RequestOptions = {},
): Promise<BlobDownload> => {
  const { method = 'GET', body, channel = 'none', query, signal, headers: extraHeaders } = options;
  const headers: Record<string, string> = { Accept: '*/*', ...extraHeaders };

  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (channel === 'platform') {
    const token = platformTokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  if (channel === 'board') {
    const token = boardTokenProvider();
    if (token) headers['X-Board-Token'] = token;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch {
    throw new ApiError(0, unavailableMessage());
  }

  if (!response.ok) {
    if (response.status === 401 && channel === 'platform') unauthorizedHandler?.();
    const payload = parsePayload(await response.text());
    throw new ApiError(response.status, extractDetail(payload) ?? responseErrorMessage(response.status));
  }

  const disposition = response.headers.get('Content-Disposition');
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition?.match(/filename="?([^";]+)"?/i)?.[1];
  let filename = plain || null;
  if (encoded) {
    try { filename = decodeURIComponent(encoded); } catch { /* Retain ordinary filename. */ }
  }

  return { blob: await response.blob(), filename };
};

export const request = apiRequest;
